import React, { useState, useContext } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Avatar,
  Menu,
  MenuItem,
  IconButton,
  Divider,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { useAuth } from "../../context/AuthContext";
import { logout as logoutAction } from "../../store/slices/authSlice";
import { clearCurrentProject } from "../../store/slices/projectSlice";
import { SidebarContext } from "./DashboardLayout";
import { APP_HEADER_HEIGHT } from "./layoutConstants";
import { ReactComponent as QuestionMarkIcon } from "../../asserts/logo/QuestionMark.svg";
import { ReactComponent as BellIcon } from "../../asserts/logo/Bell_Icon.svg";
import { ReactComponent as SettingsIcon } from "../../asserts/logo/Settings.svg";
import LogoIcon from "../../asserts/logo/Icon.svg";

function AppHeader({ showUtilityIcons = true }) {
  const dispatch = useDispatch();
  const { user, logout: keycloakLogout } = useAuth();
  const sidebarContext = useContext(SidebarContext);
  const sidebarWidth = sidebarContext?.sidebarWidth || 0;
  const [anchorEl, setAnchorEl] = useState(null);
  const username = user?.username || user?.email || "";
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleLogout = () => {
    dispatch(logoutAction());
    dispatch(clearCurrentProject());
    handleClose();
    keycloakLogout();
  };

  const getInitial = () => (username ? username.charAt(0).toUpperCase() : "U");

  const headerIconSx = {
    width: 40,
    height: 40,
    borderRadius: "8px",
    color: "#5F6368",
    "&:hover": { bgcolor: "#F5F5F5" },
  };

  return (
    <AppBar
      elevation={0}
      position="fixed"
      sx={{
        bgcolor: "#fff",
        color: "#111",
        borderBottom: "1px solid #eeeeeeff",
        zIndex: 1190,
        left: sidebarWidth || 0,
        width: sidebarWidth ? `calc(100% - ${sidebarWidth}px)` : "100%",
        transition: "left 0.2s ease, width 0.2s ease",
        height: APP_HEADER_HEIGHT,
      }}
    >
      <Toolbar
        sx={{
          justifyContent: "space-between",
          minHeight: `${APP_HEADER_HEIGHT}px !important`,
          height: APP_HEADER_HEIGHT,
          py: 0,
          boxSizing: "border-box",
          pl: sidebarWidth ? 2.5 : 2.5,
          pr: 2.5,
        }}
      >
        {!sidebarWidth && (
          <Box
            component="img"
            src={LogoIcon}
            alt="ValidAIte"
            sx={{ height: 30, width: "auto", display: "block", objectFit: "contain" }}
          />
        )}

        <Box sx={{ display: "flex", alignItems: "center", gap: showUtilityIcons ? 0.25 : 0, ml: "auto" }}>
          {showUtilityIcons && (
            <>
              <IconButton aria-label="Help" sx={headerIconSx}>
                <QuestionMarkIcon style={{ width: 20, height: 20, display: "block" }} />
              </IconButton>

              <IconButton aria-label="Notifications" sx={headerIconSx}>
                <BellIcon style={{ width: 16, height: 20, display: "block" }} />
              </IconButton>

              <IconButton aria-label="Settings" sx={{ ...headerIconSx, p: 0.5 }}>
                <SettingsIcon style={{ width: 32, height: 24, display: "block" }} />
              </IconButton>

              <Divider orientation="vertical" flexItem sx={{ mx: 1, my: 1.25, borderColor: "#E6E6E6" }} />
            </>
          )}

          <IconButton
            onClick={handleClick}
            size="small"
            aria-controls={open ? "account-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
            sx={{ p: 0.25 }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                cursor: "pointer",
                bgcolor: "#005BF0",
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              {getInitial()}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={open}
            onClose={handleClose}
            PaperProps={{
              elevation: 3,
              sx: {
                overflow: "visible",
                filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                mt: 1.5,
                "& .MuiAvatar-root": {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
                "&:before": {
                  content: '""',
                  display: "block",
                  position: "absolute",
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: "background.paper",
                  transform: "translateY(-50%) rotate(45deg)",
                  zIndex: 0,
                },
              },
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <Box sx={{ px: 2, py: 1.5, minWidth: 200 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Signed in as
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {username || "User"}
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}

export default AppHeader;
